<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-4" :key="updateKey">
        <page-header>Shopify</page-header>

        <vx-expandable-card>
            <template v-slot:header> Store </template>
            <template v-slot:headerRight>
                <vx-badge v-if="hasPermission" :color="status === 'success' ? 'success' : 'amber'">
                    {{ status === 'success' ? 'Connected' : 'Pending' }}
                </vx-badge>
            </template>
            <template v-slot:content>
                <vx-alert v-if="successMessage" color="success" shadow :closeable="false" class="mt-3">
                    {{ successMessage }}
                </vx-alert>
                <div v-if="!hasPermission" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner or admin can configure integrations.
                </div>
                <div class="flex flex-col gap-6 mt-3">
                    <div>
                        <vx-label size="md">Store Name</vx-label>
                        <vx-input
                            v-model="form.name"
                            data-test="name"
                            :disabled="loading"
                            :error="displayFirstError(validate.name.$errors) || form.errors.get('name')"
                        />
                    </div>

                    <div>
                        <vx-label size="md">Domain Name</vx-label>
                        <vx-input
                            v-model="form.domain"
                            data-test="domain"
                            :disabled="loading"
                            :error="displayFirstError(validate.domain.$errors) || form.errors.get('domain')"
                        />
                    </div>

                    <div>
                        <vx-label size="md">App Key</vx-label>
                        <vx-input
                            v-model="form.config.app_key"
                            data-test="config.app_key"
                            :disabled="loading"
                            :placeholder="storeId ? 'Leave it empty to keep the same app key.' : ''"
                            :error="
                                displayFirstError(validate.config.app_key.$errors) || form.errors.get('config.app_key')
                            "
                        />
                    </div>

                    <div>
                        <vx-label size="md">App Secret</vx-label>
                        <vx-input
                            type="password"
                            data-test="config.app_secret"
                            v-model="form.config.app_secret"
                            :disabled="loading"
                            :placeholder="storeId ? 'Leave it empty to keep the same app secret.' : ''"
                            :error="
                                displayFirstError(validate.config.app_secret.$errors) ||
                                form.errors.get('config.app_secret')
                            "
                        />
                    </div>

                    <div class="flex items-center gap-2">
                        <vx-button
                            size="lg"
                            @click="createOrUpdate()"
                            block
                            :disabled="loading"
                            data-test="create-store"
                        >
                            {{ buttonText }}
                        </vx-button>
                        <vx-button
                            v-if="storeId !== null"
                            size="lg"
                            color="danger"
                            data-test="delete-store"
                            @click="destroyStore()"
                            :disabled="loading"
                        >
                            Delete
                        </vx-button>
                    </div>
                </div>
            </template>
        </vx-expandable-card>
    </div>
</template>

<script setup>
import { shopifyClient } from '~/services';
import { VxSwal, VxButton, VxAlert, VxExpandableCard, VxBadge, VxLabel, VxInput } from '@voxie/frontend-components';
import { throwSwalRequestError } from '~/utils/request';
import { onMounted, computed, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import PageHeader from '~/components/general/PageHeader.vue';
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { displayFirstError, validateRules } from '~/utils/validation';

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
    isTeamAdmin: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const { $externalResults, catchError } = useFormRequestHandler();

const loading = ref(true);
const storeId = ref(null);
const form = ref(
    new SparkForm({
        name: null,
        domain: null,
        config: {
            app_key: null,
            app_secret: null,
        },
        message: null,
    })
);

const validate = useVuelidate(
    {
        name: {
            required: helpers.withMessage('The name field is required.', required),
            maxLength: helpers.withMessage('The name must not be greater than 56 characters.', maxLength(56)),
        },
        domain: {
            required: helpers.withMessage('The domain field is required.', required),
            regex: helpers.withMessage(
                'Must be in the format: shop-name.myshopify.com. All lowercase.',
                helpers.regex(/^[a-z0-9-]{1,80}.myshopify.com$/)
            ),
        },
        config: {
            app_key: {
                requiredIf: helpers.withMessage(
                    'The app key field is required.',
                    requiredIf(() => !storeId.value)
                ),
                maxLength: maxLength(190),
            },
            app_secret: {
                requiredIf: helpers.withMessage(
                    'The app secret field is required.',
                    requiredIf(() => !storeId.value)
                ),
                maxLength: maxLength(190),
            },
        },
    },
    form,
    { $externalResults }
);

const status = ref('pending');

const teamId = Spark.state.currentTeam.id;

const buttonText = computed(() => {
    if (storeId.value) {
        return 'Update';
    } else {
        return 'Create';
    }
});

const hasPermission = computed(() => {
    return props.ownsTeam || props.isTeamAdmin;
});

const getStore = () => {
    shopifyClient
        .getStore(teamId)
        .then((response) => {
            form.value.name = response.data.name;
            form.value.domain = response.data.domain;
            storeId.value = response.data.id;
            healthCheck();
        })
        .catch((error) => {
            if (error.response.status === 404) {
                storeId.value = null;

                form.value = new SparkForm({
                    name: null,
                    domain: null,
                    config: {},
                });

                status.value = 'pending';
            } else {
                form.value.errors = error.response ? error.response.data : error;
            }
        })
        .finally(() => (loading.value = false));
};

const healthCheck = () => {
    loading.value = true;

    shopifyClient
        .healthCheck(teamId, storeId.value)
        .then((response) => {
            status.value = response?.data?.is_healthy ? 'success' : 'pending';
        })
        .finally(() => {
            loading.value = false;
        });
};

const successMessage = ref('');
let successTimeout;
const createOrUpdate = async () => {
    clearTimeout(successTimeout);

    if (!(await validateRules(validate))) {
        return;
    }

    if (storeId.value) {
        updateStore();
    } else {
        createStore();
    }
};

const createStore = () => {
    if (!hasPermission.value) {
        permissionError();
        return;
    }
    loading.value = true;

    shopifyClient
        .createStore(teamId, form.value)
        .then((response) => {
            refreshMessages(response?.data?.message);
            storeId.value = response.data.storeId;
        })
        .catch((error) => {
            catchError(error);
        })
        .finally(() => (loading.value = false));
};

const updateStore = () => {
    if (!hasPermission.value) {
        permissionError();
        return;
    }
    loading.value = true;

    shopifyClient
        .updateStore(teamId, storeId.value, form.value)
        .then((response) => {
            refreshMessages(response?.data?.message);
        })
        .catch((error) => {
            catchError(error);
        })
        .finally(() => (loading.value = false));
};

const destroyStore = async () => {
    if (!hasPermission.value) {
        permissionError();
        return;
    }

    clearTimeout(successTimeout);

    VxSwal.confirmation('Delete Store', "Are you sure you want to delete? This action can't be undone", 'danger', () =>
        shopifyClient
            .deleteStore(teamId, storeId.value)
            .then((response) => {
                refreshMessages(response?.data?.message);

                getStore();
            })
            .catch((error) => {
                form.value.errors = error.response ? error.response.data : error;
                throwSwalRequestError(error);
            })
    );
};

const refreshMessages = (message) => {
    successMessage.value = message || '';
    form.value.errors = new Map();
    validate.value.$reset();

    successTimeout = setTimeout(() => {
        successMessage.value = '';
    }, 10000);
};

const permissionError = () => {
    toasted.error('Only the team owner or admin is allowed to create the integration.');
};

const updateKey = ref(0);
onMounted(() => {
    Bus.$on(`sparkFormUpdated.${form.value.sparkFormId}`, () => {
        updateKey.value++;
    });

    if (hasPermission.value) {
        getStore();
    }
});
</script>
